import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import { Input, Form, Modal, Select, Space, Button, message } from 'udesk-ui';
import CommonList from '../smart-words-library-common-list';
import EllipsisLnline from 'src/components/common/ellipsis-inline';
import { getAsrFilterConfigOssAsrList } from 'src/api/asr-filter-config/oss/asr/list';
import { postAsrHotWordsModels } from 'src/api/asrHotWordsModels';
import {
    getAsrHotWordsModelsById,
    putAsrHotWordsModelsById,
    deleteAsrHotWordsModelsById,
} from 'src/api/asrHotWordsModels/{id}';
import { getCurrentRouteName } from 'Udesk/system/subApp';

interface FormModel {
    asrEngine?: number;
    modelName?: string;
}

interface TemplateProps extends Record<string, any> {}

const Template: FC<TemplateProps> = (props) => {
    const listRef = useRef<any>();

    const [asrOptions, setAsrOptions] = useState<any[]>([]);

    const { refresh } = useRequest(getAsrFilterConfigOssAsrList, {
        onSuccess: (res) => {
            setAsrOptions(
                res.data?.map((d) => {
                    return {
                        value: d.value,
                        label: [d.asrType?.desc, d.languageType?.desc].join('-'),
                    };
                }) || []
            );
        },
    });

    const [visible, setVisible] = useState(false);

    const [form] = Form.useForm<FormModel>();

    const [curId, setCurId] = useState<number>();

    const onOpen = useCallback(
        (id?: number) => {
            setCurId(id);
            setVisible(true);
            if (id) {
                getAsrHotWordsModelsById({ segments: { id } }).then((res) => {
                    form.setFieldsValue({
                        modelName: res.data?.modelName,
                        asrEngine: res.data?.asrEngine,
                    });
                });
            }
        },
        [form]
    );

    const onManage = useCallback(
        (id?: number) => {
            Udesk.ui.routing.transitionTo({
                history: props.history,
                routeName: getCurrentRouteName('smartWordsLibraryHotWordsDetail'),
                pathParams: {
                    id,
                },
            });
        },
        [props.history]
    );

    const onDel = useCallback(
        (id?: number) => {
            if (id) {
                Modal.confirm({
                    title: /* 确定删除吗？ */ UdeskLocales['current'].pages.semanticIntelligence
                        .smartWordsLibrary.hotWords.index.areYouSureToDeleteIt,
                    onOk: () => {
                        deleteAsrHotWordsModelsById({ segments: { id } })
                            .then(() => {
                                message.success(
                                    /* 删除成功 */ UdeskLocales['current'].pages
                                        .semanticIntelligence.smartWordsLibrary.hotWords.index
                                        .deleteSuccessful
                                );
                            })
                            .finally(() => {
                                listRef.current.actions.refresh?.();
                                refresh();
                            });
                    },
                });
            }
        },
        [refresh]
    );

    const columns = useMemo(() => {
        return [
            {
                name: 'modelName',
                width: '10%',
                caption:
                    /* 热词模型 */ UdeskLocales['current'].pages.semanticIntelligence
                        .smartWordsLibrary.hotWords.index.hotWordModel,
            },
            {
                name: 'asrEngine',
                width: '10%',
                caption:
                    /* ASR项目 */ UdeskLocales['current'].pages.semanticIntelligence
                        .smartWordsLibrary.hotWords.index.aSRProject,
                hasTemplate: true,
                getYieldContent: (name, item, index) => {
                    const label = asrOptions.find((o) => item.asrEngine === o.value)?.label || '-';
                    return label;
                },
            },
            {
                name: 'updateTime',
                width: '10%',
                caption:
                    /* 更新时间 */ UdeskLocales['current'].pages.semanticIntelligence
                        .smartWordsLibrary.hotWords.index.updateTime,
            },
            {
                name: 'operation',
                width: '10%',
                caption:
                    /* 操作 */ UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary
                        .hotWords.index.operation,
                hasTemplate: true,
                getYieldContent: (name, item, index) => {
                    return (
                        <Space size="small">
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={() => onOpen(item.id)}
                            >
                                {/* 编辑基础信息 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .smartWordsLibrary.hotWords.index.editBasicInformation
                                }
                            </Button>
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={() => onManage(item.id)}
                            >
                                {/* 管理热词 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .smartWordsLibrary.hotWords.index.managementHotWords
                                }
                            </Button>
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                danger
                                onClick={() => onDel(item.id)}
                            >
                                {/* 删除 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .smartWordsLibrary.hotWords.index.delete
                                }
                            </Button>
                        </Space>
                    );
                },
            },
        ];
    }, [asrOptions, onDel, onManage, onOpen]);

    const [loading, setLoading] = useState(false);

    const onOk = () => {
        setLoading(true);
        form.validateFields()
            .then((values) => {
                if (curId) {
                    putAsrHotWordsModelsById(values, {
                        segments: { id: curId },
                    })
                        .then(() => {
                            message.success(
                                /* 保存成功 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .smartWordsLibrary.hotWords.index.saveSuccessful
                            );
                            onCancel();
                        })
                        .finally(() => {
                            listRef.current.actions.refresh?.();
                            setLoading(false);
                            refresh();
                        });
                } else {
                    postAsrHotWordsModels(values)
                        .then(() => {
                            message.success(
                                /* 保存成功 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .smartWordsLibrary.hotWords.index.saveSuccessful
                            );
                            onCancel();
                        })
                        .finally(() => {
                            listRef.current.actions.refresh?.();
                            setLoading(false);
                            refresh();
                        });
                }
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const onCancel = () => {
        setCurId(undefined);
        setVisible(false);
        form.setFieldsValue({
            modelName: undefined,
            asrEngine: undefined,
        });
    };

    return (
        <>
            <CommonList
                columns={columns}
                navItemType={Udesk.enums.smartWordsLibraryMenus.hotwords.id}
                getlistDataPath="asrHotWordsModels"
                openAddModal={onOpen}
                buttons={['add']}
                addText={
                    /* 新建热词模型 */ UdeskLocales['current'].pages.semanticIntelligence
                        .smartWordsLibrary.hotWords.index.newHotWordModel
                }
                onCreated={(instance) => {
                    listRef.current = instance;
                }}
                showLanguageSelect={false}
            />

            <Modal
                visible={visible}
                title={
                    curId
                        ? /* 编辑热词模型 */ UdeskLocales['current'].pages.semanticIntelligence
                              .smartWordsLibrary.hotWords.index.editHotWordModel
                        : /* 新建热词模型 */ UdeskLocales['current'].pages.semanticIntelligence
                              .smartWordsLibrary.hotWords.index.newHotWordModel
                }
                onOk={onOk}
                okButtonProps={{ loading }}
                onCancel={onCancel}
            >
                <Form labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} form={form}>
                    <Form.Item
                        label={
                            <EllipsisLnline width="100%">
                                {/* 热词模型名称 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .smartWordsLibrary.hotWords.index.hotWordModelName
                                }
                            </EllipsisLnline>
                        }
                        name="modelName"
                        rules={[
                            {
                                required: true,
                                message:
                                    /* 请输入热词模型名称 */ UdeskLocales['current'].pages
                                        .semanticIntelligence.smartWordsLibrary.hotWords.index
                                        .pleaseEnterHotwordModelName,
                            },
                        ]}
                    >
                        <Input maxLength={50} />
                    </Form.Item>

                    <Form.Item
                        label={
                            <EllipsisLnline width="100%">
                                {/* ASR项目 */}
                                {
                                    UdeskLocales['current'].pages.semanticIntelligence
                                        .smartWordsLibrary.hotWords.index.aSRProject
                                }
                            </EllipsisLnline>
                        }
                        name="asrEngine"
                        rules={[
                            {
                                required: true,
                                message:
                                    /* 请选择ASR项目 */ UdeskLocales['current'].pages
                                        .semanticIntelligence.smartWordsLibrary.hotWords.index
                                        .pleaseSelectAnASRProject,
                            },
                        ]}
                    >
                        <Select options={asrOptions} disabled={!!curId} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
